/**
 * AdminLegalDocs — Legal documents management (v50.9).
 * Access: super admin only. Each document exposes:
 *  - Download / upload / replace / delete PDF (all 6 slugs)
 *  - Full markdown editor + Export PDF (CGU + CGV only)
 */
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, FileText, Upload, Download, Trash2, Save, RefreshCw,
  Loader2, Edit3, FileDown, ScrollText,
} from 'lucide-react';
import { useAdminLang } from '../hooks/useAdminLang';

const AdminLegalDocs = () => {
  const navigate = useNavigate();
  const { at } = useAdminLang();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {slug, content_md, title, version}
  const [busy, setBusy] = useState(false);
  const fileRefs = useRef({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/admin/legal-docs');
      setDocs(r.data?.documents || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || at('legalGenericError') || 'Erreur de chargement');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const downloadPdf = async (slug) => {
    try {
      const r = await axios.get(`/api/admin/legal-docs/${slug}/download-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${slug}_destine_studio.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || at('legalDownloadKo') || 'Impossible de télécharger');
    }
  };

  const uploadPdf = async (slug, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error(at('legalMustBePdf') || 'Le fichier doit être un PDF');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`/api/admin/legal-docs/${slug}/upload-pdf`, fd);
      toast.success(at('legalUploaded') || 'PDF enregistré');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || at('legalUploadKo') || 'Erreur upload');
    } finally { setBusy(false); }
  };

  const deletePdf = async (slug) => {
    if (!window.confirm(at('legalConfirmDelete') || 'Supprimer le PDF actuel ?')) return;
    try {
      await axios.delete(`/api/admin/legal-docs/${slug}/pdf`);
      toast.success(at('legalDeleted') || 'PDF supprimé');
      await load();
    } catch { toast.error(at('legalDeleteKo') || 'Erreur suppression'); }
  };

  const openEditor = async (slug) => {
    try {
      const r = await axios.get(`/api/admin/legal-docs/${slug}`);
      setEditing(r.data);
    } catch { toast.error(at('legalGenericError') || 'Erreur'); }
  };

  const saveContent = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await axios.put(`/api/admin/legal-docs/${editing.slug}/content`, {
        content_md: editing.content_md,
        version: editing.version,
      });
      toast.success(at('legalSaved') || 'Contenu enregistré');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || at('legalSaveKo') || 'Erreur enregistrement');
    } finally { setBusy(false); }
  };

  const exportEditedAsPdf = async () => {
    if (!editing) return;
    // Ensure the latest content is saved first, then export
    await saveContent();
    try {
      const r = await axios.post(`/api/admin/legal-docs/${editing.slug}/export-pdf`, {}, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${editing.slug}_destine_studio_v${editing.version || '1'}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(at('legalPdfReady') || 'PDF prêt à télécharger');
    } catch { toast.error(at('legalExportKo') || 'Erreur export PDF'); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-950 to-purple-950/40 text-white pb-24">
      <header className="sticky top-0 z-30 bg-black/70 backdrop-blur-md border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/admin')} className="p-2 -ml-2 rounded-full hover:bg-white/10" data-testid="legal-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <ScrollText className="w-5 h-5 text-fuchsia-400" />
          <h1 className="font-bold text-base sm:text-lg flex-1">{at('legalDocsTitle') || 'Documents juridiques'}</h1>
          <button onClick={load} className="p-2 rounded-full hover:bg-white/10" data-testid="legal-refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 pt-4 space-y-3">
        <p className="text-xs text-white/60 mb-2">{at('legalDocsSub') || 'Gère les PDF et les contenus juridiques de l\u2019app. Les CGU et CGV sont éditables directement ici.'}</p>

        {loading ? (
          <div className="p-8 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-fuchsia-400" /></div>
        ) : docs.map((d) => (
          <div key={d.slug} className="p-4 bg-white/5 border border-white/10 rounded-2xl space-y-2" data-testid={`legal-doc-${d.slug}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-fuchsia-300 shrink-0" />
                  <h3 className="font-semibold text-sm truncate">{d.title}</h3>
                </div>
                <div className="text-[10px] text-white/50 mt-0.5">
                  v{d.version} · {d.has_pdf ? (at('legalPdfPresent') || 'PDF présent') : (at('legalNoPdf') || 'Aucun PDF')} · {d.has_content ? (at('legalTextPresent') || 'contenu texte OK') : (at('legalNoText') || 'sans texte')}
                </div>
              </div>
              {d.editable && (
                <span className="px-2 py-0.5 text-[9px] uppercase tracking-wider rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">{at('legalEditable') || 'Éditable'}</span>
              )}
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <input
                ref={(el) => (fileRefs.current[d.slug] = el)}
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={(e) => uploadPdf(d.slug, e.target.files?.[0])}
                data-testid={`legal-upload-input-${d.slug}`}
              />
              <button
                onClick={() => fileRefs.current[d.slug]?.click()}
                disabled={busy}
                className="px-3 py-1.5 text-xs rounded-full bg-fuchsia-500/20 hover:bg-fuchsia-500/30 text-fuchsia-200 inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid={`legal-upload-btn-${d.slug}`}
              >
                <Upload className="w-3.5 h-3.5" /> {d.has_pdf ? (at('legalReplace') || 'Remplacer PDF') : (at('legalUpload') || 'Uploader PDF')}
              </button>

              {d.has_pdf && (
                <>
                  <button
                    onClick={() => downloadPdf(d.slug)}
                    className="px-3 py-1.5 text-xs rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center gap-1.5"
                    data-testid={`legal-download-btn-${d.slug}`}
                  >
                    <Download className="w-3.5 h-3.5" /> {at('legalDownload') || 'Télécharger'}
                  </button>
                  <button
                    onClick={() => deletePdf(d.slug)}
                    className="px-3 py-1.5 text-xs rounded-full bg-red-500/20 hover:bg-red-500/30 text-red-200 inline-flex items-center gap-1.5"
                    data-testid={`legal-delete-btn-${d.slug}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> {at('legalDeletePdf') || 'Supprimer PDF'}
                  </button>
                </>
              )}

              {d.editable && (
                <button
                  onClick={() => openEditor(d.slug)}
                  className="px-3 py-1.5 text-xs rounded-full bg-purple-500/20 hover:bg-purple-500/30 text-purple-200 inline-flex items-center gap-1.5"
                  data-testid={`legal-edit-btn-${d.slug}`}
                >
                  <Edit3 className="w-3.5 h-3.5" /> {at('legalEdit') || 'Modifier le contenu'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Full-screen editor for CGU / CGV */}
      {editing && (
        <div className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-sm flex flex-col" data-testid="legal-editor-modal">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
            <button onClick={() => setEditing(null)} className="p-2 -ml-2 rounded-full hover:bg-white/10" data-testid="legal-editor-close">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <FileText className="w-5 h-5 text-fuchsia-400" />
            <h2 className="font-bold text-sm truncate flex-1">{editing.title}</h2>
            <input
              value={editing.version || ''}
              onChange={(e) => setEditing({ ...editing, version: e.target.value })}
              placeholder="v50.9"
              className="w-20 bg-white/10 border border-white/15 rounded-lg px-2 py-1 text-[11px] text-white text-center"
              data-testid="legal-editor-version"
            />
          </div>

          <textarea
            value={editing.content_md || ''}
            onChange={(e) => setEditing({ ...editing, content_md: e.target.value })}
            spellCheck={false}
            className="flex-1 w-full bg-black/40 text-white text-sm font-mono px-4 py-3 leading-relaxed outline-none resize-none border-0"
            placeholder={at('legalMdPlaceholder') || 'Contenu Markdown (## Titre, - liste, **gras**)…'}
            data-testid="legal-editor-textarea"
          />

          <div className="flex flex-wrap gap-2 justify-end px-4 py-3 border-t border-white/10 bg-black/60">
            <button
              onClick={saveContent}
              disabled={busy}
              className="px-4 py-2 text-sm rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="legal-editor-save"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {at('legalSave') || 'Enregistrer'}
            </button>
            <button
              onClick={exportEditedAsPdf}
              disabled={busy}
              className="px-4 py-2 text-sm rounded-full bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:brightness-110 text-white inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="legal-editor-export-pdf"
            >
              <FileDown className="w-4 h-4" />
              {at('legalExportPdf') || 'Enregistrer & Télécharger le PDF'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLegalDocs;
